import { CSSProperties, ReactNode, useState } from "react";
import { ChevronDown, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  DropiState,
  MastershopState,
  Order,
  ShopifyState,
} from "../../SalesPage/SalesPageInterface";
import { CustomerInterface } from "../OrderDetailsInterface";
import { dictionary, Language } from "../../../utils/dictionary";
import { getErrorMessage, handleApi } from "../../../utils/handleApi";
import { retryMastershopOrder, retryShopifyOrder } from "../../../services/orders/orderAxios";
import { formatCurrency, formatDateInZone, getCurrencySymbol } from "../../../utils/formatters";
import { INTEGRATIONS_UI_CONFIG } from "../../../components/integrations/config/integrations.config";
import { DropiRetryCard } from "../../../components/cards/DropiRetryCard";
import { StatusDot, StatusTone } from "@/components/status/StatusDot";
import { getLogisticsTone } from "@/components/status/statusTones";
import { MastershopRetryModal } from "./MastershopRetryModal";
import { splitIntegrationError } from "./orderTotals";

const MASTERSHOP_ORDERS_URL = "https://app.mastershop.com/operational-management/orders";

const SHOPIFY_STATUS_KEY: Record<string, string> = {
  [ShopifyState.PENDING]: "shopify-status-pending",
  [ShopifyState.PENDING_SYNC]: "shopify-status-pending",
  [ShopifyState.PENDING_RATE]: "shopify-status-pending-rate",
  [ShopifyState.RETRYING]: "shopify-status-retrying",
  [ShopifyState.SUCCESS]: "shopify-status-success",
  [ShopifyState.ERROR_RETRYABLE]: "shopify-status-error-retryable",
  [ShopifyState.ERROR_FINAL]: "shopify-status-error-final",
  [ShopifyState.ERROR]: "shopify-status-error",
};

const MASTERSHOP_STATUS_KEY: Record<string, string> = {
  [MastershopState.PENDING_SYNC]: "mastershop-status-pending-sync",
  [MastershopState.WAITING_PAYMENT]: "mastershop-status-waiting-payment",
  [MastershopState.SUCCESS]: "mastershop-status-success",
  [MastershopState.ERROR]: "mastershop-status-error",
};

interface DetailRow {
  label: string;
  value: string;
}

interface IntegrationRowData {
  key: string;
  logoSrc: string;
  name: string;
  tone: StatusTone;
  statusLabel: string;
  summary?: string;
  detailRows: DetailRow[];
  error?: string | null;
  action?: ReactNode;
  externalLink?: { href: string; label: string };
  // Nota corta al lado del estado (ej. "Orden 2161180"): para datos sueltos
  // que no ameritan abrir un detalle colapsable.
  inlineNote?: string;
  defaultExpanded?: boolean;
  rank: number;
}

const ExternalLinkAction = ({ href, label }: { href: string; label: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-fluxi transition-colors hover:text-fluxi-dark"
  >
    {label}
    <ExternalLink className="h-3.5 w-3.5" />
  </a>
);

const ActionLinkButton = ({
  onClick,
  loading,
  label,
  loadingLabel,
}: {
  onClick: () => void;
  loading: boolean;
  label: string;
  loadingLabel: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-fluxi transition-colors hover:text-fluxi-dark disabled:cursor-not-allowed disabled:opacity-60"
  >
    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
    {loading ? loadingLabel : label}
  </button>
);

const addDetailRow = (rows: DetailRow[], label: string, value?: unknown) => {
  const text = value == null ? "" : String(value).trim();
  if (!text) return;
  rows.push({ label, value: text });
};

const formatSummaryValue = (value: unknown) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
};


const IntegrationRow = ({ row, language }: { row: IntegrationRowData; language: Language }) => {
  const [expanded, setExpanded] = useState(Boolean(row.defaultExpanded));
  const hasDetail = row.detailRows.length > 0;

  return (
    <div className="py-3 first:pt-2 last:pb-0">
      <div className="flex items-center gap-3">
        <img
          src={row.logoSrc}
          alt={row.name}
          className="shrink-0 rounded-lg border border-gray-100 bg-white object-contain p-0.5"
          style={{ width: 28, height: 28, minWidth: 28 }}
        />
        <button
          type="button"
          onClick={() => hasDetail && setExpanded((value) => !value)}
          className={`flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-left ${
            hasDetail ? "" : "cursor-default"
          }`}
        >
          <span className="text-sm font-semibold text-gray-900">{row.name}</span>
          <StatusDot tone={row.tone} label={row.statusLabel} />
          {row.inlineNote && (
            <span className="text-xs text-gray-400">· {row.inlineNote}</span>
          )}
        </button>
        {row.externalLink && (
          <ExternalLinkAction href={row.externalLink.href} label={row.externalLink.label} />
        )}
        {row.action}
        {hasDetail && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-label={
              expanded
                ? dictionary["hide-sync-detail"][language]
                : dictionary["view-sync-detail"][language]
            }
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {row.summary && <p className="mt-1 pl-10 text-xs leading-4 text-gray-500">{row.summary}</p>}
      {row.error && <p className="mt-1 pl-10 text-xs leading-5 text-red-600">{row.error}</p>}

      {hasDetail && (
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-out ${
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <dl className="ml-10 mt-2 space-y-1 pb-0.5">
              {row.detailRows.map((detail) => (
                <div key={detail.label} className="flex items-baseline justify-between gap-3 text-xs">
                  <dt className="text-gray-500">{detail.label}</dt>
                  <dd className="text-right font-medium text-gray-900 tabular-nums">{detail.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

export const OrderIntegrationsCard = ({
  order,
  customer,
  dropiError,
  timezone,
  language,
  onShopifyRetryComplete,
  onMastershopRetryComplete,
  onDropiSynced,
  className = "",
  style,
}: {
  order: Order;
  customer?: CustomerInterface;
  dropiError?: string;
  timezone: string;
  language: Language;
  onShopifyRetryComplete: () => void;
  onMastershopRetryComplete: () => void;
  onDropiSynced: () => void;
  className?: string;
  style?: CSSProperties;
}) => {
  const [isRetryingShopify, setIsRetryingShopify] = useState(false);
  const [isRetryingMastershop, setIsRetryingMastershop] = useState(false);
  const [showMastershopRetryModal, setShowMastershopRetryModal] = useState(false);

  const rows: IntegrationRowData[] = [];

  // ── Dropi ──
  const isDropiRetryable =
    order.dropi_state === DropiState.ERROR || order.dropi_state === DropiState.PENDING_SYNC;
  const dropiLogistics = order.dropi_logistics;
  const shouldShowDropi =
    order.dropi_state === DropiState.SUCCESS ||
    isDropiRetryable ||
    Boolean(dropiLogistics?.dropi_order_id) ||
    Boolean(dropiLogistics?.dropi_observed_status_normalized) ||
    Boolean(dropiLogistics?.last_error);
  if (shouldShowDropi) {
    const isDropiSuccess = order.dropi_state === DropiState.SUCCESS;
    const isDropiErrorState = order.dropi_state === DropiState.ERROR;
    const dropiDetailRows: DetailRow[] = [];
    const dropiErrorParts = splitIntegrationError(dropiError || order.dropi_integration_error);
    addDetailRow(dropiDetailRows, "Estado logistico", dropiLogistics?.status_label || order.logistics_status_label);
    addDetailRow(dropiDetailRows, "Estado Dropi observado", dropiLogistics?.dropi_observed_status_normalized || order.dropi_observed_status_normalized || order.dropi_observed_status);
    addDetailRow(dropiDetailRows, "Orden Dropi", dropiLogistics?.dropi_order_id);
    addDetailRow(dropiDetailRows, "Transportadora", dropiLogistics?.carrier);
    addDetailRow(dropiDetailRows, "Guia", dropiLogistics?.guide_number);
    addDetailRow(dropiDetailRows, "Sticker", dropiLogistics?.sticker_file);
    if (dropiLogistics?.last_tracking_at) {
      addDetailRow(
        dropiDetailRows,
        "Ultimo tracking",
        formatDateInZone(dropiLogistics.last_tracking_at, timezone)
      );
    }
    if (dropiLogistics?.quote_summary) {
      Object.entries(dropiLogistics.quote_summary).forEach(([key, value]) => {
        addDetailRow(
          dropiDetailRows,
          key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
          formatSummaryValue(value)
        );
      });
    }
    const logisticsDecision = dropiLogistics?.logistics_decision;
    addDetailRow(dropiDetailRows, "Transportadora elegida", logisticsDecision?.carrier_name);
    addDetailRow(dropiDetailRows, "ID transportadora", logisticsDecision?.carrier_distribution_company_id);
    addDetailRow(dropiDetailRows, "Oficina logistica", logisticsDecision?.office_address);
    addDetailRow(dropiDetailRows, "Ciudad oficina", logisticsDecision?.office_city);
    addDetailRow(dropiDetailRows, "Departamento oficina", logisticsDecision?.office_department);
    if (logisticsDecision?.small_municipality_rule_applied != null) {
      addDetailRow(
        dropiDetailRows,
        "Regla municipio pequeno",
        logisticsDecision.small_municipality_rule_applied ? "Aplicada" : "No aplicada"
      );
    }
    if (logisticsDecision?.expensive_shipping_rule_applied != null) {
      addDetailRow(
        dropiDetailRows,
        "Regla envio caro",
        logisticsDecision.expensive_shipping_rule_applied ? "Aplicada" : "No aplicada"
      );
    }
    addDetailRow(dropiDetailRows, "Recargo cliente", logisticsDecision?.customer_extra_shipping_charge);
    const dropiStatusLabel = dropiLogistics?.status_label || order.logistics_status_label;
    const dropiStatusTone = getLogisticsTone(dropiLogistics?.status_code || order.logistics_status_code, order.dropi_state);
    const dropiErrorMessage = dropiLogistics?.last_error || (!isDropiSuccess ? dropiErrorParts.message : undefined);

    rows.push({
      key: "dropi",
      logoSrc: INTEGRATIONS_UI_CONFIG.dropi.logoSrc,
      name: INTEGRATIONS_UI_CONFIG.dropi.name,
      tone: dropiStatusTone,
      statusLabel: dropiStatusLabel ||
        (isDropiSuccess
          ? dictionary["integration-status-synced"][language]
          : isDropiErrorState
          ? dictionary["integration-status-error"][language]
          : dictionary["integration-status-pending"][language]),
      summary: isDropiSuccess
        ? dictionary["integration-dropi-description-success"][language]
        : isDropiErrorState
          ? dictionary["integration-dropi-description-error"]?.[language]
          : dictionary["integration-dropi-description-pending"]?.[language],
      detailRows: dropiDetailRows,
      error: dropiErrorMessage,
      externalLink:
        isDropiSuccess && order.dropi_url
          ? { href: order.dropi_url, label: dictionary["integration-dropi-button-text"][language] }
          : undefined,
      action:
        !isDropiSuccess && customer ? (
          <DropiRetryCard
            compact
            orderId={order.id}
            dropiState={order.dropi_state}
            customer={customer}
            error={dropiError}
            onSynced={onDropiSynced}
          />
        ) : undefined,
      defaultExpanded: isDropiErrorState,
      rank: isDropiSuccess ? 2 : isDropiErrorState ? 0 : 1,
    });
  }

  // ── Shopify ──
  const shopifyState = order.shopify_integration_state;
  if (shopifyState) {
    const originalCurrency = order.shopify_original_currency || order.currency_code || "COP";
    const originalSymbol = getCurrencySymbol(originalCurrency);
    const isUsdConversion = order.shopify_currency_mode === "USD_CONVERSION";
    const sentCurrency = order.shopify_currency_sent || (isUsdConversion ? "USD" : originalCurrency);
    const sentSymbol = sentCurrency === "USD" ? "US$" : getCurrencySymbol(sentCurrency);
    const hasAppliedUsdRate =
      isUsdConversion &&
      shopifyState === ShopifyState.SUCCESS &&
      order.shopify_rate_used != null &&
      order.shopify_total_sent != null;
    const isShopifyError =
      shopifyState === ShopifyState.ERROR_RETRYABLE ||
      shopifyState === ShopifyState.ERROR_FINAL ||
      shopifyState === ShopifyState.ERROR;
    const isShopifySuccess = shopifyState === ShopifyState.SUCCESS;
    const canRetryShopify = isShopifyError || shopifyState === ShopifyState.PENDING_RATE;
    const syncUpdatedAt = order.updated_at || order.created_at;

    const shopifySummary = (() => {
      if (shopifyState === ShopifyState.PENDING_RATE)
        return dictionary["shopify-sync-pending-rate-description"][language];
      if (shopifyState === ShopifyState.ERROR_RETRYABLE)
        return dictionary["shopify-sync-retryable-description"][language];
      if (shopifyState === ShopifyState.ERROR_FINAL)
        return dictionary["shopify-sync-final-description"][language];
      if (isShopifySuccess) return dictionary["shopify-sync-success-description"][language];
      if (isShopifyError) return dictionary["shopify-sync-error-description"][language];
      return dictionary["shopify-sync-pending-description"][language];
    })();

    const shopifyDetailRows: DetailRow[] = [
      {
        label: dictionary["sync-mode-label"][language],
        value: isUsdConversion
          ? dictionary["sync-mode-usd"][language]
          : dictionary["sync-mode-funnel-currency"][language],
      },
      {
        label: dictionary["sync-original-order"][language],
        value: `${formatCurrency(
          Number(order.shopify_original_total ?? order.total_value_with_discount ?? 0),
          originalSymbol
        )} ${originalCurrency}`,
      },
    ];
    if (hasAppliedUsdRate) {
      shopifyDetailRows.push({
        label: dictionary["sync-rate-used"][language],
        value: `1 ${originalCurrency} = ${Number(order.shopify_rate_used).toFixed(6)} USD`,
      });
      if (order.shopify_rate_date) {
        shopifyDetailRows.push({
          label: dictionary["sync-rate-date"][language],
          value: order.shopify_rate_date,
        });
      }
    }
    if (isShopifySuccess && syncUpdatedAt) {
      shopifyDetailRows.push({
        label: dictionary["sync-synced-label"][language],
        value: formatDateInZone(syncUpdatedAt, timezone),
      });
    }
    if (isShopifySuccess && order.shopify_total_sent != null) {
      shopifyDetailRows.push({
        label: dictionary["sync-sent-label"][language],
        value: `${formatCurrency(Number(order.shopify_total_sent), sentSymbol)} ${sentCurrency}`,
      });
    }
    if (order.shopify_retry_count != null && order.shopify_retry_count > 0) {
      shopifyDetailRows.push({
        label: dictionary["sync-attempts"][language],
        value: String(order.shopify_retry_count),
      });
    }
    if (order.shopify_next_retry_at) {
      shopifyDetailRows.push({
        label: dictionary["sync-next-retry"][language],
        value: formatDateInZone(order.shopify_next_retry_at, timezone),
      });
    }
    if (order.shopify_last_error_code) {
      shopifyDetailRows.push({
        label: dictionary["sync-error-code"][language],
        value: order.shopify_last_error_code,
      });
    }

    const handleRetryShopify = async () => {
      if (isRetryingShopify || !order.id) return;
      setIsRetryingShopify(true);
      const { res, error } = await handleApi(retryShopifyOrder(order.id), false);
      setIsRetryingShopify(false);

      if (error) {
        toast.error(getErrorMessage(error));
        return;
      }

      if (res?.accepted) {
        toast.success(dictionary["shopify-retry-queued"][language]);
      } else if (res?.success) {
        toast.success(dictionary["shopify-retry-synced"][language]);
      } else {
        toast(dictionary["shopify-retry-executed"][language]);
      }
      onShopifyRetryComplete();
    };

    rows.push({
      key: "shopify",
      logoSrc: INTEGRATIONS_UI_CONFIG.shopify.logoSrc,
      name: INTEGRATIONS_UI_CONFIG.shopify.name,
      tone: isShopifySuccess ? "success" : isShopifyError ? "error" : "warning",
      statusLabel: SHOPIFY_STATUS_KEY[shopifyState]
        ? dictionary[SHOPIFY_STATUS_KEY[shopifyState]][language]
        : shopifyState,
      summary: shopifySummary,
      detailRows: shopifyDetailRows,
      error: isShopifyError
        ? splitIntegrationError(order.shopify_integration_error).message
        : undefined,
      externalLink: order.shopify_admin_url
        ? { href: order.shopify_admin_url, label: dictionary["view-in-shopify"][language] }
        : undefined,
      action: canRetryShopify ? (
        <ActionLinkButton
          onClick={handleRetryShopify}
          loading={isRetryingShopify}
          label={dictionary["retry-sync"][language]}
          loadingLabel={dictionary["retrying-sync"][language]}
        />
      ) : undefined,
      defaultExpanded: isShopifyError,
      rank: isShopifySuccess ? 2 : isShopifyError ? 0 : 1,
    });
  }

  // ── Mastershop ──
  const mastershopState =
    order.mastershop_state ||
    (order.mastershop_integration_error ? MastershopState.ERROR : undefined);
  if (mastershopState) {
    const isMsSuccess = mastershopState === MastershopState.SUCCESS;
    const isMsError = mastershopState === MastershopState.ERROR;
    const isMsPending =
      mastershopState === MastershopState.PENDING_SYNC ||
      mastershopState === MastershopState.WAITING_PAYMENT;

    const msSummary = (() => {
      if (isMsSuccess) return dictionary["mastershop-sync-success-description"][language];
      if (isMsError) return dictionary["mastershop-sync-error-description"][language];
      if (mastershopState === MastershopState.WAITING_PAYMENT)
        return dictionary["mastershop-sync-waiting-payment-description"][language];
      return dictionary["mastershop-sync-pending-description"][language];
    })();

    const msErrorParts = splitIntegrationError(order.mastershop_integration_error);
    // Mastershop no devuelve tracking ni más datos: el order id (referencia
    // para buscarla en su panel) va inline junto al estado, sin dropdown.
    const msInlineNote =
      isMsSuccess && order.mastershop_order_id
        ? `${dictionary["order-ref-short"][language]} ${order.mastershop_order_id}`
        : undefined;

    const handleRetryMastershop = async () => {
      if (isRetryingMastershop || !order.id) return;
      setIsRetryingMastershop(true);
      const { error } = await handleApi(retryMastershopOrder(order.id), false);
      setIsRetryingMastershop(false);

      if (error) {
        toast.error(getErrorMessage(error));
        return;
      }

      toast.success(dictionary["mastershop-retry-success"][language]);
      onMastershopRetryComplete();
    };

    rows.push({
      key: "mastershop",
      logoSrc: INTEGRATIONS_UI_CONFIG.mastershop.logoSrc,
      name: INTEGRATIONS_UI_CONFIG.mastershop.name,
      tone: isMsSuccess ? "success" : isMsError ? "error" : "warning",
      statusLabel: MASTERSHOP_STATUS_KEY[mastershopState]
        ? dictionary[MASTERSHOP_STATUS_KEY[mastershopState]][language]
        : mastershopState,
      summary: msSummary,
      detailRows: [],
      inlineNote: msInlineNote,
      error: msErrorParts.message,
      externalLink: isMsSuccess
        ? { href: MASTERSHOP_ORDERS_URL, label: dictionary["view-in-mastershop"][language] }
        : undefined,
      // Con cliente disponible, el retry abre el modal de edición (los
      // rechazos de Mastershop suelen ser por datos del cliente: reintentar
      // sin corregirlos fallaría igual). Sin cliente, retry directo.
      action: isMsError ? (
        customer ? (
          <ActionLinkButton
            onClick={() => setShowMastershopRetryModal(true)}
            loading={false}
            label={dictionary["mastershop-retry-button"][language]}
            loadingLabel={dictionary["mastershop-retrying-button"][language]}
          />
        ) : (
          <ActionLinkButton
            onClick={handleRetryMastershop}
            loading={isRetryingMastershop}
            label={dictionary["mastershop-retry-button"][language]}
            loadingLabel={dictionary["mastershop-retrying-button"][language]}
          />
        )
      ) : undefined,
      defaultExpanded: isMsError || isMsPending,
      rank: isMsSuccess ? 2 : isMsError ? 0 : 1,
    });
  }

  if (rows.length === 0) return null;

  // Lo accionable primero: errores arriba, luego pendientes, luego sincronizadas.
  rows.sort((a, b) => a.rank - b.rank);

  return (
    <div
      className={`rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_2px_12px_rgba(112,144,176,0.08)] ${className}`}
      style={style}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {dictionary["integrations-section"][language]}
      </p>
      <div className="mt-1 divide-y divide-gray-100">
        {rows.map((row) => (
          <IntegrationRow key={row.key} row={row} language={language} />
        ))}
      </div>

      {showMastershopRetryModal && customer && (
        <MastershopRetryModal
          orderId={order.id}
          customer={customer}
          onClose={() => setShowMastershopRetryModal(false)}
          onSynced={onMastershopRetryComplete}
        />
      )}
    </div>
  );
};
